import { FINALIZED_STATUSES } from "../constants/observations";
import { makeStandardLens } from "./standardScope";

// DRF serializes DecimalField as a STRING, so every percentage read off an
// analytics payload has to be coerced before it can be rounded, compared or
// sorted — while keeping null (no data) distinct from 0%.
export const num = (v) => (v == null ? null : Number(v));

// The product-wide scoring window: only the latest COUNTED_OBSERVATION_LIMIT
// finalized observations from the past COUNTED_WINDOW_DAYS count toward a
// teacher's "current" scores. Every surface that claims an observation is
// (or is not) part of the score must derive that from
// selectCountedObservations — never re-implement the window.
// Mirrored on the backend by observations/services/counted_window.py —
// keep the two in sync.
export const COUNTED_OBSERVATION_LIMIT = 3;
// Kept in sync with observations/services/counted_window.py. The day-window is
// currently DISABLED (see selectCountedObservations) per a manager request, so
// this constant is retained only for when the window is re-enabled.
export const COUNTED_WINDOW_DAYS = 28;
// const COUNTED_WINDOW_MS = COUNTED_WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Observation matrices display the counted window plus one older observation
// for context; the extra column renders dimmed so it reads as history, not
// score input. Shared by the teacher overview modal and the teacher dashboard.
export const DISPLAYED_OBSERVATION_LIMIT = COUNTED_OBSERVATION_LIMIT + 1;

// Single definition of which observations are "counted". Accepts either
// /observations/ list rows or detail payloads (both carry status +
// observed_at). Returns the counted subset sorted oldest → newest.
export const selectCountedObservations = (observations) => {
  if (!observations?.length) return [];
  // const now = Date.now(); // re-enable with the window check below
  return observations
    .filter((o) => {
      if (!FINALIZED_STATUSES.has(o.status) || !o.observed_at) return false;
      // Time window DISABLED per manager request (2026-06): score on the latest
      // COUNTED_OBSERVATION_LIMIT finalized observations regardless of age.
      // Re-enable by restoring COUNTED_WINDOW_MS above and returning:
      //   return now - new Date(o.observed_at).getTime() <= COUNTED_WINDOW_MS;
      return true;
    })
    .sort((a, b) => new Date(a.observed_at) - new Date(b.observed_at))
    .slice(-COUNTED_OBSERVATION_LIMIT);
};

// Recency weights (1, 2, 3 oldest → newest) shared by the overall and
// per-standard aggregations so one observation can never count toward one
// number but not the other.
const weightedAverage = (entries) => {
  let totalWeighted = 0;
  let totalWeight = 0;
  entries.forEach(({ value, weight }) => {
    totalWeighted += value * weight;
    totalWeight += weight;
  });
  return totalWeight ? totalWeighted / totalWeight : null;
};

// Weighted average of `percentage` across the counted observations. Operates
// on the `/observations/` list shape ({ status, observed_at, percentage }).
// Returns 0-100, or null when nothing qualifies — "no recent data" must never
// render as a literal 0%.
export const computeOverallScoreFromApi = (observations) => {
  const counted = selectCountedObservations(observations).filter(
    (o) => o.percentage != null,
  );
  return weightedAverage(
    counted.map((o, i) => ({ value: Number(o.percentage), weight: i + 1 })),
  );
};

// Weighted per-standard averages across the counted subset of the given
// observation DETAIL payloads (must carry standard_scores), read through
// today's rubric `standards` (see makeStandardLens). Same window and weighting
// as computeOverallScoreFromApi. Returns { [standardUuid]: 0-100 } keyed by
// `standards` — those with no counted score are absent.
export const computeStandardScoresFromDetails = (details, standards) => {
  const lens = makeStandardLens(standards);
  const counted = selectCountedObservations(details);
  const perStandard = {};
  counted.forEach((obs, i) => {
    (obs.standard_scores || []).forEach((s) => {
      if (s.percentage == null) return;
      const standard = lens(s.standard_lineage, obs);
      if (!standard) return;
      (perStandard[standard.uuid] = perStandard[standard.uuid] || []).push({
        value: Number(s.percentage),
        weight: i + 1,
      });
    });
  });
  const result = {};
  Object.entries(perStandard).forEach(([uuid, entries]) => {
    result[uuid] = weightedAverage(entries);
  });
  return result;
};
